import React, { useMemo } from 'react';
import SvgIcon from '../../utils/SvgIcon';

import InvoiceResults from './InvoiceResults';
import { InvoiceOverviewResponse } from '../../../types/dashboard';
import { useQuery } from '@tanstack/react-query';
import { useDashboardConfig } from '../hooks/useDashboardConfig';

export default function Invoices() {
  const { labels, invoiceEndpoint, urlPayCta, urlInvoiceCta } = useDashboardConfig();
  const { isPending, error, data } = useQuery({
    queryKey: ['dashboardInovices'],
    queryFn: () => fetch(invoiceEndpoint).then(res => res.json() as Promise<InvoiceOverviewResponse>),
  });

  const inCredit = useMemo(() => {
    if (!data?.overview) return false;
    return data.overview.outstandingBalance < 0;
  }, [data]);

  if (error) {
    console.error('Invoices | Error when fetching invoice data', error);
    return null;
  }

  // Show skeleton while loading
  if (isPending) {
    return (
      <article className="dashboard-card dashboard-card--with-footer dashboard-invoices">
        <div className="dashboard-card__inner">
          <h2 className="dashboard-invoices__title">{labels.invoiceTitle}</h2>
          <Skeleton />
        </div>
      </article>
    );
  }

  if (data?.overview == null) {
    return null;
  }

  // Hide component if account is suspended
  if (data?.isAccountSuspended) {
    return null;
  }

  const renderInvoiceContent = () => {
    if (data?.overview?.invoices?.length > 0) {
      return (
        <InvoiceResults
          invoices={data.overview.invoices}
          payUrl={urlPayCta}
          invoicePageUrl={data.invoicePageUrl}
          labels={labels}
          showViewAll={data.showViewAll}
        />
      );
    }

    return <NoneFound noInvoicesLabel={labels.invoiceNone} />;
  };

  return (
    <article className="dashboard-card dashboard-card--with-footer dashboard-invoices">
      <div className="dashboard-card__inner">
        <h2 className="dashboard-invoices__title">
          {labels.invoiceTitle}
          <span className="dashboard-invoices__title-balance">£{data?.overview?.outstandingBalance}</span>
          {inCredit ? <span className="dashboard-invoices__title-in-credit">In credit</span> : null}
        </h2>

        {renderInvoiceContent()}
      </div>

      <div className="dashboard-card__footer">
        <p className="dashboard-invoices__footnote">{labels.invoiceIssue}</p>
        {urlInvoiceCta !== '' ? (
          <a href={urlInvoiceCta} className="cta cta--small cta--secondary">
            {labels.invoiceCta}
            <SvgIcon iconName="arrow-right" className="icon cta__icon" />
          </a>
        ) : null}
      </div>
    </article>
  );
}

function Skeleton() {
  return (
    <div className="dashboard-invoices__skeleton">
      <div className="skeleton"></div>
      <div className="skeleton"></div>
      <div className="skeleton"></div>
    </div>
  );
}

function NoneFound({
  noInvoicesLabel,
}: Readonly<{
  noInvoicesLabel: string;
}>) {
  return (
    <div className="dashboard-invoices__none">
      <div className="dashboard-invoices__none-icon">
        <SvgIcon iconName="thumbs-up" />
      </div>
      <p className="dashboard-invoices__none-text">{noInvoicesLabel}</p>
    </div>
  );
}
